import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "../../utils/cn";

interface OTPInputProps {
  length: number;
  onChange: (otp: string) => void;
  value: string;
  onComplete?: (otp: string) => void;
  password?: boolean; // Optional prop to toggle password visibility
}

const FleetrifyOTPInput: React.FC<OTPInputProps> = ({
  length,
  onChange,
  value,
  onComplete,
  password = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [inputType, setInputType] = useState(password ? "password" : "text"); // Default to "text" if no password prop is provided

  useEffect(() => {
    inputRefs.current = Array(length)
      .fill(null)
      .map((_, i) => inputRefs.current[i] || null);
  }, [length]);

  useEffect(() => {
    for (let i = 0; i < length; i++) {
      if (!value[i] && inputRefs.current[i]) {
        inputRefs.current[i]?.focus();
        break;
      }
    }
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newValue = e.target.value;
    if (/^\d?$/.test(newValue)) {
      const otpArray = value.split("");
      otpArray[index] = newValue;
      const newOTP = otpArray.join("");
      onChange(newOTP);
      if (newValue && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
      // If password prop is enabled, switch to password field after input
      if (newValue && password) {
        setInputType("password");
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, ""); // remove non-digits
    if (!paste) return;

    const otpArray = value.split("");
    let i = index;

    for (let char of paste) {
      if (i >= length) break;
      otpArray[i] = char;
      i++;
    }

    const newOTP = otpArray.join("");
    onChange(newOTP);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2">
      {Array.from({ length }).map((_, index) => (
        <motion.div
          key={index}
          initial={false}
          animate={{
            scale: value[index] ? 1.1 : 1,
            transition: { type: "spring", stiffness: 300, damping: 15 },
          }}
        >
          <input
            type={inputType} // Use password type if prop is passed, otherwise text
            maxLength={1}
            value={value[index] || ""}
            onChange={(e) => handleInputChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            className={cn(
              "w-12 h-12 text-center text-xl font-bold",
              "rounded-md border !text-[#00032A] border-gray-300",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
              "transition-all duration-150",
              "appearance-none",
              "bg-blue-100"
            )}
            inputMode="numeric"
            pattern="\d*"
            aria-label={`OTP input ${index + 1}`}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default FleetrifyOTPInput;
